import { ILegalEntitySelectOption } from '@/sections/administrative/accounts/orders/hooks';
import { MilestoneToBeBilled } from '@/services/billing/types/billing.type';
import { OrganizationDoc } from '@/types/entities';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { groupMilestonesBySubAndOrderType } from '../utils/billing.utils';
import {
  CreateBillingOrderRequest,
  CreateDiscountRequest,
} from '@/services/billing/types/create-billing.type';
import { SecondStepFormValues } from '../components/form/billing-order-new-edit-secondstep-form';

export interface SubscriptionItem {
  totalAmount: number;
  milestones: MilestoneToBeBilled[];
}

export interface LegalEntityDistribution {
  legalEntity: ILegalEntitySelectOption;
  subscriptionItems: SubscriptionItem[];
}

export interface OrganizationResume
  extends Pick<OrganizationDoc, '_id' | 'name' | 'country' | 'owner' | 'segment'> {}

type StoreState = {
  organization: OrganizationResume | null;
  selectedMilestones: MilestoneToBeBilled[];
  legalEntityDistributions: LegalEntityDistribution[];
  billingOrder: CreateBillingOrderRequest | null;
  discounts: CreateDiscountRequest[];
  secondStepFormValues: SecondStepFormValues[];

  setOrganization: (organization: Partial<OrganizationResume>) => void;
  selectAllMonth: (milestones: MilestoneToBeBilled[]) => void;
  deselectAllMonth: (milestones: MilestoneToBeBilled[]) => void;
  selectMilestone: (milestone: MilestoneToBeBilled) => void;
  deselectMilestone: (milestoneId: string) => void;
  deselectAllMilestones: () => void;
  setBillingOrder: (billingOrder: CreateBillingOrderRequest) => void;
  addLegalEntityDistribution: (
    legalEntities: ILegalEntitySelectOption[]
  ) => LegalEntityDistribution[];
  addDiscount: (discount: CreateDiscountRequest) => void;
  removeDiscount: (index: number) => void;
  setSecondStepFormValues: (secondStepFormValues: SecondStepFormValues[]) => void;
  clearDiscounts: () => void;
  clearStore: () => void;
};

/**
 * Zustand store for managing billing order state
 * Persists state to localStorage
 */
export const useBillingStore = create<StoreState>()(
  persist(
    (set, get) => ({
      organization: null,
      selectedMilestones: [],
      legalEntityDistributions: [],
      billingOrder: null,
      discounts: [],
      secondStepFormValues: [],
      setOrganization: (organization) => {
        const currentOrg = get().organization;
        set({
          organization: { ...currentOrg, ...organization } as OrganizationResume,
        });
      },

      /**
       * Selects all milestones for a given month
       * Uses immutable update pattern
       *
       * @param milestones - Array of milestones to select
       */
      selectAllMonth: (milestones) => {
        const currentSelected = get().selectedMilestones;

        const currentIds = new Set(currentSelected.map((m) => m._id));

        // Only add milestones that aren't already selected
        const newMilestones = milestones.filter((m) => !currentIds.has(m._id));

        set({ selectedMilestones: [...currentSelected, ...newMilestones] });
      },

      /**
       * Deselects all milestones for a given month
       *
       * @param milestones - Array of milestones to deselect
       */
      deselectAllMonth: (milestones) => {
        const currentSelected = get().selectedMilestones;
        const milestoneIds = new Set(milestones.map((m) => m._id));

        const updatedSelected = currentSelected.filter(
          (selected) => !milestoneIds.has(selected._id)
        );

        set({ selectedMilestones: updatedSelected });
      },

      /**
       * Selects a single milestone
       * Uses immutable update pattern
       *
       * @param milestone - The milestone to select
       */
      selectMilestone: (milestone) => {
        const currentSelected = get().selectedMilestones;
        const isAlreadySelected = currentSelected.some((m) => m._id === milestone._id);

        if (!isAlreadySelected) {
          set({ selectedMilestones: [...currentSelected, milestone] });
        }
      },

      /**
       * Deselects a single milestone
       *
       * @param milestone - The milestone to deselect
       */
      deselectMilestone: (milestoneId) => {
        const currentSelected = get().selectedMilestones;
        const updatedSelected = currentSelected.filter((m) => m._id !== milestoneId);

        set({ selectedMilestones: updatedSelected });
      },

      /**
       * Deselects all milestones
       *
       */
      deselectAllMilestones: () => {
        set({ selectedMilestones: [], legalEntityDistributions: [] });
      },

      /**
       * Sets the billing order
       *
       * @param billingOrder - The billing order to set
       */
      setBillingOrder: (billingOrder) => {
        set({ billingOrder });
      },
      /**
       * Adds legal entity distributions for the selected milestones
       *
       * @param legalEntities - Array of legal entities to distribute the milestones to
       */
      addLegalEntityDistribution: (legalEntities) => {
        const selectedMilestones = get().selectedMilestones;
        const groupedMilestones = groupMilestonesBySubAndOrderType(selectedMilestones);
        const legalEntityDistributions: LegalEntityDistribution[] = [];
        for (const legalEntity of legalEntities) {
          const SubscriptionItems: SubscriptionItem[] = [];
          for (const groupedMilestone of groupedMilestones) {
            let totalAmount = 0;
            groupedMilestone.milestones.forEach((milestone) => {
              totalAmount += milestone.total;
            });
            SubscriptionItems.push({
              totalAmount,
              milestones: groupedMilestone.milestones,
            });
          }
          legalEntityDistributions.push({ legalEntity, subscriptionItems: SubscriptionItems });
        }
        set({ legalEntityDistributions });
        return legalEntityDistributions;
      },
      addDiscount: (discount) => {
        const currentDiscounts = get().discounts;
        set({ discounts: [...currentDiscounts, discount] });
      },
      removeDiscount: (index) => {
        const currentDiscounts = get().discounts;
        set({ discounts: currentDiscounts.filter((_, i) => i !== index) });
      },
      clearDiscounts: () => set({ discounts: [] }),
      setSecondStepFormValues: (secondStepFormValues) => {
        set({ secondStepFormValues });
      },

      clearStore: () =>
        set({
          organization: null,
          selectedMilestones: [],
          legalEntityDistributions: [],
          discounts: [],
          billingOrder: null,
          secondStepFormValues: [],
        }),
    }),

    {
      name: 'billing-order-store',
    }
  )
);
